import { defineStore } from "pinia";
import { setupSyncInterval } from "@/util/syncInterval";
import axios from "axios";
import { CONFIG_ENDPOINT } from "@/constants/Endpoints";
import { LOGGER } from "@/util/logger";
import auth from "@/auth";
import router from "@/router";

export const useConnectivityStore = defineStore("connectivity", {
  state: () => ({
    toggledOnline: true,
    serviceStatus: false,
    offlineAt: globalThis.navigator.onLine ? null : new Date(),
    isOnline: globalThis.navigator.onLine,
    isLoading: false,
  }),
  getters: {
    getOnlineServiceStatus() {
      return this.isOnline && this.serviceStatus && this.toggledOnline;
    },
    getisOnline() {
      return this.isOnline && this.serviceStatus;
    },
    getIsLoading() {
      return this.isLoading;
    },
    getToggledOnline() {
      return this.toggledOnline;
    },
  },
  actions: {
    async toggleOnline(value) {
      this.toggledOnline = value;
      if (!value) {
        router.push({ name: "OfflineLogin" });
      }
      this.offlineAt = this.getisOnline ? null : new Date();
    },
    toggleOnlineNoRedirect(value) {
      this.toggledOnline = value;
      this.offlineAt = this.getisOnline ? null : new Date();
    },
    async getServiceStatus() {
      try {
        const response = await axios.get(CONFIG_ENDPOINT.PING_ACTUATOR, {
          timeout: 5000, // Set timeout to 5000 milliseconds (5 seconds)
        });
        this.serviceStatus =
          (response.status >= 200 && response.status < 300) || response.status === 304 || response.status === 401;
      } catch (e) {
        // 401 means the server is reached but failed to authorize/JWT not present.
        this.serviceStatus = e?.response?.data?.statusCode === 401;

        LOGGER.logException(Object.assign(e, { method: "pingActuator" }));
      }
    },
    addListeners() {
      const { onOffline, onOnline } = callbacks(this);
      globalThis.addEventListener("offline", onOffline);
      globalThis.addEventListener("online", onOnline);
      if ("connection" in globalThis.navigator) {
        this.offlineAt = this.getisOnline ? null : new Date();
      }
    },
    removeListeners() {
      const { onOffline, onOnline } = callbacks(this);
      globalThis.removeEventListener("offline", onOffline);
      globalThis.removeEventListener("online", onOnline);
    },
    setIsLoading(value) {
      this.isLoading = value;
    },
  },
});

function callbacks(instance) {
  let timer;
  const onOffline = () => {
    instance.isOnline = globalThis.navigator.onLine;
    instance.serviceStatus = false;
    instance.offlineAt = new Date();
    clearTimeout(timer);
  };
  const onOnline = async () => {
    instance.isOnline = globalThis.navigator.onLine;
    timer = setTimeout(setOnline, 5 * 60 * 1000);
  };
  const setOnline = async () => {
    await instance.getServiceStatus();
    instance.offlineAt = null;
    await auth.silentLogin();
    setupSyncInterval();
    clearTimeout(timer);
  };
  return { onOffline, onOnline };
}
